import { Spinner } from "@brexhq/component-library";
import { Global, type CSSObject } from "@emotion/react";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React from "react";

import breakpoints from "@brexhq/shared/styles/breakpoints";
import { defineResponsiveSpacingVariable } from "@brexhq/shared/styles/spacing";
import { mobile } from "@brexhq/shared/types/SpacingScale";
import { useRefCallback } from "@brexhq/shared/utils/useRefCallback";

import useAnalytics from "../../../hooks/useAnalytics";
import {
  DEFAULT_H_PADDING,
  MOBILE_H_PADDING,
} from "../../../routes/support/Layout";
import {
  SupportChatClosedEvent,
  SupportChatOpenedEvent,
  SupportChatTriggeredEvent,
} from "../../../telemetry/analyticsEvents";

import { SupportChatContext } from "./useSupportChat";

const SupportChatPanel = React.lazy(async () => ({
  default: (await import("./SupportChatPanel")).SupportChatPanel,
}));

const StyledButton = styled.button`
  all: unset;
  background-color: black;
  border-radius: 40px;
  padding: 12px 16px;

  display: flex;
  gap: 8px;
  place-items: center;
  place-content: center;

  position: sticky;
  bottom: 48px;
  margin-bottom: 48px;

  z-index: ${({ theme }) => theme.zIndex.goku};

  ${defineResponsiveSpacingVariable("--margin", "xlrg")}
  margin-bottom: var(--margin);
  margin-left: auto;

  ${defineResponsiveSpacingVariable("--right-margin", DEFAULT_H_PADDING)}
  ${breakpoints.SML} {
    --right-margin: ${mobile[MOBILE_H_PADDING]}px;
  }
  margin-right: var(--right-margin);
`;

const StyledText = styled.span`
  background: linear-gradient(123deg, #ffbc96 0%, #91ecea 99.04%);
  background-clip: text;
  -webkit-text-fill-color: transparent;
  ${({ theme }) => theme.typography.HeadlineSmallSB}
  ${breakpoints.SML} {
    ${({ theme }) => theme.typography.BodySmallSB}
  }
`;

const Wrapper = styled.div`
  position: relative;
`;

/**
 * we're inlining it instead of using Metal's Icon because it needs to have a gradient as the
 * `path`s' `fill`, which we can't achieve in SVG through CSS, having to add <defs><linearGradient>
 * markup instead
 */
const HelpIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    className={className}
    aria-label="Help icon"
    role="img"
  >
    <path
      d="M12.6875 21.4213C12.6875 20.4203 13.499 19.6088 14.5 19.6088C15.501 19.6088 16.3125 20.4203 16.3125 21.4213C16.3125 22.4224 15.501 23.2338 14.5 23.2338C13.499 23.2338 12.6875 22.4224 12.6875 21.4213Z"
      fill="url(#gradient-fill)"
    />
    <path
      d="M14.5 6.31718C11.8306 6.31718 9.66667 8.48114 9.66667 11.1505H12.0833C12.0833 9.81582 13.1653 8.73385 14.5 8.73385C15.8347 8.73385 16.9167 9.81582 16.9167 11.1505V11.6271C16.9167 12.4352 16.5128 13.1897 15.8405 13.6379L15.4439 13.9023C14.0993 14.7987 13.2917 16.3078 13.2917 17.9239V18.4005H15.7083V17.9239C15.7083 17.1159 16.1122 16.3613 16.7845 15.9131L17.1811 15.6487C18.5257 14.7523 19.3333 13.2432 19.3333 11.6271V11.1505C19.3333 8.48114 17.1694 6.31718 14.5 6.31718Z"
      fill="url(#gradient-fill)"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.5 29.2755C22.5081 29.2755 29 22.7836 29 14.7755C29 6.76738 22.5081 0.275513 14.5 0.275513C6.49187 0.275513 0 6.76738 0 14.7755C0 22.7836 6.49187 29.2755 14.5 29.2755ZM14.5 26.8588C21.1734 26.8588 26.5833 21.449 26.5833 14.7755C26.5833 8.10207 21.1734 2.69218 14.5 2.69218C7.82656 2.69218 2.41667 8.10207 2.41667 14.7755C2.41667 21.449 7.82656 26.8588 14.5 26.8588Z"
      fill="url(#gradient-fill)"
    />
    <defs>
      <linearGradient
        id="gradient-fill"
        x1="0"
        y1="0.275513"
        x2="33.8367"
        y2="21.6615"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFBC96" />
        <stop offset="1" stopColor="#91ECEA" />
      </linearGradient>
    </defs>
  </svg>
);

const IconWrapper = styled.div`
  --icon-size: 32px;
  width: var(--icon-size);
  height: var(--icon-size);
  display: flex;
  place-items: center;
  place-content: center;
  position: relative;

  ${breakpoints.SML} {
    --icon-size: 16px;
  }
`;

const MessageIndicator = styled.div`
  background-color: #ff4444;
  height: 10px;
  width: 10px;
  position: absolute;
  border-radius: 50%;
  top: -2px;
  right: -2px;
  z-index: 1;
`;

const StyledHelpIcon = styled(HelpIcon)`
  width: 100%;
  height: 100%;
`;

const globalStyles: CSSObject = {
  "#ada-button-frame": {
    display: "none",
  },
  "#ada-chat-frame": {
    ...defineResponsiveSpacingVariable("--padding", "xlrg"),
    right: "var(--padding) !important",
    bottom: "var(--padding) !important",
    [breakpoints.SML]: {
      right: "0 !important",
      bottom: "0 !important",
    },
  },
};

export const SupportChatGlobalStyles = () => <Global styles={globalStyles} />;

export const SupportChatProvider = ({
  hideButton,
  children,
}: {
  hideButton?: boolean;
  children: React.ReactNode;
}) => {
  const { trackEvent } = useAnalytics();

  const stableTrackEvent = useRefCallback(trackEvent);

  const openStateReducer: React.Reducer<
    {
      isOpen: boolean;
      isLoading: boolean;
      unreadCount: number;
      hasBeenOpened: boolean;
    },
    "toggle" | "loaded" | boolean | "increment_unread"
  > = (prevState, action) => {
    let newIsOpen: boolean = prevState.isOpen;
    let newIsLoading: boolean = prevState.isLoading;
    let newUnreadCount: number = prevState.unreadCount;
    let newHasBeenOpened: boolean = prevState.hasBeenOpened;
    if (action === "toggle") {
      newIsOpen = !prevState.isOpen;
      newIsLoading = !newHasBeenOpened && newIsOpen;
      newHasBeenOpened = true;
      if (newIsOpen) {
        newUnreadCount = 0;
      }
    } else if (action === "loaded") {
      newIsLoading = false;
    } else if (action === "increment_unread") {
      newUnreadCount = newUnreadCount + 1;
    } else {
      newIsOpen = action;
      if (newIsOpen) {
        newUnreadCount = 0;
      }
    }
    if (newIsOpen !== prevState.isOpen) {
      if (newIsOpen) {
        stableTrackEvent(new SupportChatTriggeredEvent());
      } else {
        stableTrackEvent(new SupportChatClosedEvent());
      }
    }
    return {
      isOpen: newIsOpen,
      isLoading: newIsLoading,
      unreadCount: newUnreadCount,
      hasBeenOpened: newHasBeenOpened,
    };
  };

  const [{ isOpen, isLoading, unreadCount, hasBeenOpened }, dispatch] =
    React.useReducer(openStateReducer, {
      isOpen: false,
      isLoading: false,
      unreadCount: 0,
      hasBeenOpened: false,
    });

  // Audio notification setup
  const notificationAudioRef = React.useRef<HTMLAudioElement | null>(null);

  // Initialize audio notification
  React.useEffect(() => {
    const audio = new Audio("/sounds/livechat-unread-message-notification.mp3");
    audio.preload = "auto";
    audio.volume = 0.5;
    notificationAudioRef.current = audio;

    return () => {
      if (notificationAudioRef.current) {
        notificationAudioRef.current.pause();
        notificationAudioRef.current.src = "";
        notificationAudioRef.current = null;
      }
    };
  }, []);

  const playNotificationSound = React.useCallback(() => {
    const audio = notificationAudioRef.current;
    if (audio) {
      audio.currentTime = 0;
      void audio.play().catch((error) => {
        console.log("Error playing notification sound: ", error);
      });
    }
  }, []);

  const handleAgentMessage = React.useCallback(() => {
    if (!isOpen) {
      dispatch("increment_unread");
      playNotificationSound();
    }
  }, [isOpen, playNotificationSound]);

  const onClose = React.useCallback(() => dispatch(false), []);
  const ctx = React.useMemo(
    () => ({ openSupportChat: () => dispatch(true) }),
    [],
  );

  const router = useRouter();

  React.useEffect(() => {
    if (router.query.chat === "true") {
      ctx.openSupportChat();
      const search = new URLSearchParams(window.location.search);
      search.delete("chat");
      router.replace(
        { ...window.location, search: search.toString() },
        undefined,
        { shallow: true, scroll: false },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this when the chat param changes
  }, [router.query.chat]);

  const onLoad = React.useCallback(() => {
    dispatch("loaded");
    stableTrackEvent(new SupportChatOpenedEvent());
  }, [stableTrackEvent]);

  const onToggle = React.useCallback(() => dispatch("toggle"), []);

  return (
    <SupportChatContext.Provider value={ctx}>
      <Wrapper>
        {children}
        {!hideButton && (
          <>
            <StyledButton type="button" onClick={onToggle}>
              <IconWrapper>
                {unreadCount > 0 && <MessageIndicator />}
                {isLoading ? (
                  <Spinner size={"100%" as any} />
                ) : (
                  <StyledHelpIcon />
                )}
              </IconWrapper>{" "}
              <StyledText>Chat</StyledText>
            </StyledButton>
            {hasBeenOpened && (
              <React.Suspense fallback={null}>
                <SupportChatPanel
                  showChat={isOpen && !isLoading}
                  onClose={onClose}
                  onLoad={onLoad}
                  onAgentMessage={handleAgentMessage}
                />
              </React.Suspense>
            )}
          </>
        )}
      </Wrapper>
    </SupportChatContext.Provider>
  );
};
